import { useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  EllipsisVertical,
  Eye,
  EyeOff,
  Hourglass,
  KeyRound,
  Send,
  UserMinus2,
  UserPlus2,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { SettingsSection } from "src/components/SettingsDialog/SettingsSection";
import { UserAvatar } from "src/components/ui/avatar";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { ScrollArea, ScrollBar } from "src/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { useAuth } from "src/lib/auth";
import { useAdminPrivacyMode } from "src/lib/hooks/useAdminPrivacyMode";
import { useApiMutation } from "src/lib/hooks/useApi";
import {
  INVITE_USER_TO_INTEGRATION_MUTATION,
  REMOVE_USER_FROM_INTEGRATION_MUTATION,
  UPDATE_USER_ROLE_IN_INTEGRATION_MUTATION,
} from "src/lib/queries/integrations";
import { cn } from "src/lib/utils";

const inviteFormSchema = z.object({
  email: z.string().email("Invalid email").or(z.literal("")),
});

const TeamSection = ({ store, setPreventClickOutside, isAdmin }) => {
  const { currentUser } = useAuth();
  const { privacyMode } = useAdminPrivacyMode();
  const [invitePopoverOpen, setInvitePopoverOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [revealedMembers, setRevealedMembers] = useState(() => new Set());

  const inviteForm = useForm({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const { mutationApi: sendInvite, loading: inviteUserLoading } =
    useApiMutation({
      mutation: INVITE_USER_TO_INTEGRATION_MUTATION,
    });
  const { mutationApi: updateRole, loading: updateRoleLoading } =
    useApiMutation({
      mutation: UPDATE_USER_ROLE_IN_INTEGRATION_MUTATION,
    });
  const { mutationApi: deleteMember, loading: removeUserLoading } =
    useApiMutation({
      mutation: REMOVE_USER_FROM_INTEGRATION_MUTATION,
    });
  const loading = inviteUserLoading || updateRoleLoading || removeUserLoading;

  const confirmDeleteMember = async () => {
    if (!memberToDelete) return;
    const toastId = toast.loading(
      `Removing ${memberToDelete.name || memberToDelete.email}...`
    );

    const { data } = await deleteMember({
      id: store.id,
      userOrInviteId: memberToDelete.id,
    });

    if (data) {
      setMemberToDelete(null);

      toast.success(
        `${memberToDelete.name || memberToDelete.email}'s ${
          memberToDelete.isInvite ? "invite" : "membership"
        } has been removed.`,
        {
          id: toastId,
        }
      );
    } else {
      toast.error(
        `Failed to remove ${
          memberToDelete.name || memberToDelete.email
        }. Please try again`,
        {
          id: toastId,
        }
      );
    }
  };

  const onInviteSubmit = async ({ email }, resend) => {
    const toastId = toast.loading(
      `${resend ? "Resending" : "Sending"} invite to ${email}...`
    );

    const { data, error } = await sendInvite({
      id: store.id,
      email,
      role: "MEMBER",
    });

    if (data) {
      inviteForm.reset();
      setInvitePopoverOpen(false);
      setPreventClickOutside(false);

      toast.success(`${resend ? "Resent" : "Sent"} invite to ${email}`, {
        id: toastId,
      });
    } else {
      const errorMessage =
        error?.message ||
        `Failed to ${resend ? "resend" : "send"} invite. Please try again`;

      toast.error(errorMessage, {
        id: toastId,
      });
      inviteForm.setError("email", {
        message: errorMessage,
      });
    }
  };

  const onRoleChange = async (member, newRole) => {
    const toastId = toast.loading(
      `Updating role for ${member.name || member.email} to ${newRole}...`
    );

    const { data } = await updateRole({
      id: store.id,
      userId: member.id,
      role: newRole.toUpperCase(),
    });

    if (data) {
      toast.success(
        `Role updated to ${newRole} for ${member.name || member.email}`,
        {
          id: toastId,
        }
      );
    } else {
      toast.error(
        `Failed to update role for ${member.name || member.email}. Please try again`,
        {
          id: toastId,
        }
      );
    }
  };

  const members = useMemo(() => {
    return [
      // map users
      ...store.users
        .filter((user) => !user.hidden)
        .map((user) => {
          return {
            ...user.user,
            role: user.role,
          };
        }),
      // map invites
      ...store.invites.map((invite) => ({
        ...invite,
        isInvite: true,
      })),
    ].sort((a, b) => {
      // put current user at the top
      if (b.id === currentUser.id) {
        return 1;
      }

      if (a.role === b.role) {
        return (a.name || a.email)?.localeCompare(b.name || b.email) || 0;
      }
      return a.role === "ADMIN" ? -1 : 1;
    });
  }, [currentUser, store]);

  const isMemberRevealed = (memberId) => {
    if (!privacyMode) return true;
    return revealedMembers.has(memberId);
  };

  const toggleMemberReveal = (memberId) => {
    setRevealedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  const getMaskedMemberName = (member, revealed) => {
    if (revealed) {
      return member.name || "Pending...";
    }
    return "Hidden member";
  };

  const getMaskedMemberEmail = (member, revealed) => {
    if (revealed) {
      return member.email || "-";
    }
    return "*****";
  };

  return (
    <SettingsSection
      title="Team Members"
      description={
        (isAdmin ? "Manage" : "View") + " your team members and their roles"
      }
    >
      {isAdmin && (
        <Popover
          modal
          open={invitePopoverOpen}
          onOpenChange={(state) => {
            setInvitePopoverOpen(state);
            setPreventClickOutside(state);

            if (!state) {
              inviteForm.reset();
            }
          }}
        >
          <PopoverTrigger asChild inert={invitePopoverOpen}>
            <Button
              size="sm"
              variant="outline"
              className="top-4 right-4 sm:absolute"
            >
              <UserPlus2 className="mx-1 size-4" />
              Invite Member
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end">
            <div className="flex flex-col space-y-2 text-center sm:text-left">
              <h3 className="text-lg font-semibold">Invite a new member</h3>
              <p className="text-muted-foreground text-sm">
                Enter the email address of the person you want to invite to your
                team.
              </p>
            </div>
            <Form {...inviteForm}>
              <form onSubmit={inviteForm.handleSubmit(onInviteSubmit)}>
                <FormField
                  control={inviteForm.control}
                  name="email"
                  disabled={loading}
                  render={({ field }) => (
                    <>
                      <div className="flex items-center space-x-2 pt-4">
                        <div className="grid flex-1 gap-2">
                          <FormItem>
                            <FormLabel className="sr-only">Email</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder={
                                  "john.doe@" +
                                  (currentUser.email?.split("@")?.[1] ||
                                    "example.com")
                                }
                                className="h-8"
                              />
                            </FormControl>
                          </FormItem>
                        </div>

                        <Button
                          size="sm"
                          disabled={!inviteForm.getValues("email") || loading}
                        >
                          <span className="sr-only">Send invite</span>
                          <Send />
                        </Button>
                      </div>

                      <FormMessage className="mt-2" />
                    </>
                  )}
                />
              </form>
            </Form>
          </PopoverContent>
        </Popover>
      )}

      <ScrollArea>
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                {isAdmin && <TableHead className="w-[50px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => {
                const isSelf = member.id === currentUser.id;
                const invitedRecently =
                  new Date(member.updatedAt) > new Date() - 1000 * 60 * 5;
                const revealed = isMemberRevealed(member.id);
                const memberName = getMaskedMemberName(member, revealed);
                const memberEmail = getMaskedMemberEmail(member, revealed);

                return (
                  <TableRow key={member.id}>
                    <TableCell>
                      <div className="flex max-w-20 items-center gap-2 sm:max-w-60">
                        <UserAvatar
                          className="hidden sm:block"
                          user={revealed ? member : null}
                          fallbackIcon={revealed ? Hourglass : EyeOff}
                        />
                        <span
                          className={cn(
                            "overflow-hidden overflow-ellipsis",
                            (!revealed || !member.name) &&
                              "text-muted-foreground"
                          )}
                        >
                          {memberName}
                        </span>
                        {privacyMode && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => toggleMemberReveal(member.id)}
                            aria-label={
                              revealed ? "Hide member" : "Show member"
                            }
                          >
                            {revealed ? (
                              <EyeOff className="size-4" />
                            ) : (
                              <Eye className="size-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      className={member.isInvite && "text-muted-foreground"}
                    >
                      {memberEmail}
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Select
                          onOpenChange={setPreventClickOutside}
                          onValueChange={(newRole) =>
                            onRoleChange(member, newRole)
                          }
                          defaultValue={member.role.toLowerCase()}
                          disabled={isSelf}
                        >
                          <SelectTrigger className="col-span-2">
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">
                              <span className="flex items-center gap-2">
                                <KeyRound className="size-3" />
                                Admin
                              </span>
                            </SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>
                          {member.role.charAt(0).toUpperCase() +
                            member.role.slice(1).toLowerCase()}
                        </span>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Popover modal onOpenChange={setPreventClickOutside}>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <EllipsisVertical />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto max-w-80">
                            <div className="grid w-full gap-4">
                              <div className="w-full space-y-1 overflow-hidden">
                                <div className="overflow-hidden leading-none font-medium overflow-ellipsis whitespace-nowrap">
                                  {revealed
                                    ? member.name || member.email
                                    : "Hidden member"}
                                  {member.isInvite && (
                                    <span className="text-muted-foreground text-sm">
                                      {" "}
                                      (Pending)
                                    </span>
                                  )}
                                </div>
                                <p className="text-muted-foreground text-sm">
                                  {member.isInvite
                                    ? "Invitation sent on"
                                    : "Last seen on"}{" "}
                                  {new Date(
                                    member.updatedAt
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                  })}
                                </p>
                              </div>

                              {member.isInvite && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    onInviteSubmit(
                                      {
                                        email: member.email,
                                      },
                                      true
                                    )
                                  }
                                  disabled={loading || invitedRecently}
                                >
                                  <Send />
                                  {invitedRecently
                                    ? "Invite sent"
                                    : "Resend invite"}
                                </Button>
                              )}
                              {member.isInvite && invitedRecently && (
                                <p className="text-muted-foreground -mt-2 text-xs italic">
                                  Please wait 5 minutes before resending
                                </p>
                              )}

                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setMemberToDelete(member);
                                }}
                                disabled={isSelf || loading}
                              >
                                <UserMinus2 />
                                {member.isInvite
                                  ? "Cancel invite"
                                  : "Remove member"}
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Delete member confirmation dialog */}
      <Dialog
        open={!!memberToDelete}
        onOpenChange={() => {
          setMemberToDelete(null);
        }}
      >
        <DialogContent>
          <DialogTitle>
            {memberToDelete?.isInvite ? "Cancel Invite" : "Remove Member"}
          </DialogTitle>
          <DialogDescription>
            {memberToDelete && (
              <>
                Are you sure you want to{" "}
                {memberToDelete.isInvite
                  ? "cancel the invite for"
                  : "remove the member"}{" "}
                <strong>
                  {isMemberRevealed(memberToDelete.id)
                    ? memberToDelete.name || memberToDelete.email
                    : "Hidden member"}
                </strong>
                ? This action cannot be undone.
              </>
            )}
          </DialogDescription>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMemberToDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteMember}>
              {memberToDelete?.isInvite ? "Cancel Invite" : "Remove Member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
};

export default TeamSection;
